import cx from 'classnames'
import { type ChangeEvent } from 'react'

import { type SanityFilterGroupOption } from '@data/sanity/queries/types/cart'
import { type UrlParameter } from '@lib/hooks'
import { type Filter } from '@lib/product'

import { CheckboxSvg } from '@components/checkbox'
import Swatch from '@components/swatch'

interface CollectionFilterOptionProps {
  option: SanityFilterGroupOption
  onChange: (newParameters: UrlParameter[]) => void
  isGrid: boolean
  activeFilter?: Filter
}

const CollectionFilterOption = ({
  option,
  activeFilter,
  isGrid,
  onChange,
}: CollectionFilterOptionProps) => {
  const activeFilterValues = activeFilter?.values ?? []
  const isChecked = activeFilterValues.includes(option.slug.current)
  const isSwatch = option.type === 'swatch'

  const handleFilterChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!activeFilter) {
      return
    }

    const newValues = activeFilterValues.includes(target.value)
      ? activeFilterValues.filter((value) => value !== target.value)
      : [...activeFilterValues, target.value]

    onChange([
      {
        name: activeFilter.name,
        value: newValues.length > 0 ? newValues.join() : null,
      },
    ])
  }

  return (
    <div
      className={cx('w-full', {
        'inline-flex flex-col w-auto ml-1 mr-5 text-center': isSwatch,
        'border text-center transition-colors duration-200': isGrid,
        'bg-pageText text-pageBG border-pageText': isGrid && isChecked,
      })}
    >
      <div className="flex flex-col relative text-left">
        <input
          id={`filter-${option.slug.current}`}
          name={activeFilter?.name}
          type="checkbox"
          value={option.slug.current}
          checked={isChecked}
          onChange={handleFilterChange}
          className="sr-only"
        />
        <label
          htmlFor={`filter-${option.slug.current}`}
          className={cx('relative cursor-pointer', {
            'inline-flex': !isGrid,
            'block text-center p-2': isGrid,
            'flex flex-col items-center': isSwatch,
          })}
        >
          <div className={cx('relative', { hidden: isGrid })}>
            {isSwatch && option.color && (
              <Swatch
                label={option.title}
                color={option.color}
                isActive={isChecked}
                className="w-[40px] h-[40px]"
              >
                <CheckboxSvg
                  isChecked={isChecked}
                  className={cx(
                    'absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-transparent border-none',
                    {
                      'bg-transparent': isChecked && isSwatch,
                    },
                  )}
                  pathStyle={{ color: 'var(--swatchBorder)' }}
                />
              </Swatch>
            )}
            {!isSwatch && (
              <CheckboxSvg
                isChecked={isChecked}
                className={cx({
                  'delay-150': !isChecked,
                  'delay-0': isChecked,
                })}
              />
            )}
          </div>
          <div
            className={cx('mt-1 text-xs font-semibold', {
              'mt-2': isSwatch,
            })}
          >
            {option.title}
          </div>
        </label>
      </div>
    </div>
  )
}

export default CollectionFilterOption
